import { useCallback, useEffect, useRef, useState } from "react";
import Axios from "axios";
import { toast } from "react-toastify";
import { api_url } from "../../../config";
import dataService from "../../../helpers/dataService";

// Données de l'espace allocataire. Même enchaînement d'appels que l'ancien
// tableau de bord : droits, questionnaire et fiche en parallèle ; la fiche
// déclenche le parcours, puis la vérification du RDO (qui renvoie vers le
// questionnaire s'il manque, sauf allocataire importé du stock) ; le RDO
// déclenche ses réponses et, si les droits sont ouverts, l'orientation ; un
// rendez-vous existant déclenche la liste des rendez-vous.
export default function useEspaceAllocataire({ userId, onQuestionnaireManquant, onAllocataire }) {
  const [droitsOuverts, setDroitsOuverts] = useState(null); // null : pas encore connu
  const [formulaire, setFormulaire] = useState(null);
  const [allocataire, setAllocataire] = useState(null);
  const [evenements, setEvenements] = useState(null);
  const [rdspId, setRdspId] = useState(null);
  const [hasRdv, setHasRdv] = useState(false);
  const [rdvs, setRdvs] = useState(null);
  const [isLoadingRdvs, setIsLoadingRdvs] = useState(false);
  const [reponses, setReponses] = useState(null);
  const [organisme, setOrganisme] = useState(null);
  const [errors, setErrors] = useState({});
  const [etapes, setEtapes] = useState({ droits: false, fiche: false, verification: false });

  // Les rappels du parent ne doivent pas relancer le chargement initial.
  const rappels = useRef({ onQuestionnaireManquant, onAllocataire });
  rappels.current = { onQuestionnaireManquant, onAllocataire };

  const terminer = (etape) => setEtapes((e) => ({ ...e, [etape]: true }));

  const chargerRdvs = useCallback(() => {
    setIsLoadingRdvs(true);
    dataService.get("rdv/mine", setRdvs, setErrors, () => setIsLoadingRdvs(false));
  }, []);

  const verifierRdo = useCallback((origine) => {
    Axios.get(api_url + "rdsp/mine/check")
      .then((res) => {
        const { rdsp, rdv } = res.data;
        if (rdsp) {
          setRdspId(rdsp);
        } else if (origine !== "STOCK" && rappels.current.onQuestionnaireManquant) {
          rappels.current.onQuestionnaireManquant();
        }
        setHasRdv(!!rdv);
      })
      .catch(() => {
        toast.error(
          "Une erreur s'est produite pendant la récupération de vos informations, essayez de vous déconnecter"
        );
      })
      .then(() => terminer("verification"));
  }, []);

  const chargerParcours = useCallback(
    (origine) => {
      dataService.get("recipients/me/timeline", setEvenements, setErrors, () => verifierRdo(origine));
    },
    [verifierRdo]
  );

  const chargerFiche = useCallback(() => {
    dataService.get(
      "recipients/me/",
      (data) => {
        setAllocataire(data);
        if (rappels.current.onAllocataire) rappels.current.onAllocataire(data);
        chargerParcours(data.origin);
      },
      setErrors,
      () => terminer("fiche")
    );
  }, [chargerParcours]);

  const chargerDroits = useCallback(() => {
    dataService.get(
      "recipients/" + userId + "/rights",
      (data) => setDroitsOuverts(!!data.isRightsOpen),
      () => {},
      () => terminer("droits")
    );
  }, [userId]);

  useEffect(() => {
    chargerDroits();
    dataService.get("rdsp", setFormulaire, setErrors);
    chargerFiche();
  }, [chargerDroits, chargerFiche]);

  useEffect(() => {
    if (rdspId) dataService.get("rdsp/" + rdspId, setReponses, setErrors);
  }, [rdspId]);

  // L'orientation n'existe qu'une fois les droits ouverts : l'API répond une
  // erreur sinon, qui s'affichait en rouge sur tous les espaces sans droits.
  useEffect(() => {
    if (rdspId && droitsOuverts === true) {
      dataService.get("rdsp/" + rdspId + "/orientation", (d) => setOrganisme(d.organization), setErrors);
    }
  }, [rdspId, droitsOuverts]);

  useEffect(() => {
    if (hasRdv) chargerRdvs();
  }, [hasRdv, chargerRdvs]);

  // Après une prise ou un report de rendez-vous : parcours et vérification RDO
  // rechargés, comme avant.
  const rafraichir = useCallback(() => {
    chargerParcours(allocataire ? allocataire.origin : undefined);
  }, [chargerParcours, allocataire]);

  const pret = etapes.droits && etapes.fiche && (etapes.verification || !allocataire);

  return {
    pret,
    droitsOuverts,
    formulaire,
    allocataire,
    evenements,
    rdspId,
    hasRdv,
    rdvs,
    isLoadingRdvs,
    reponses,
    organisme,
    errors,
    rafraichir,
    chargerRdvs,
  };
}
